import { Chip, FormControlLabel, Grid, Link } from "@mui/material";
import { AddSharp } from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxControlledTable from "components/UI/AcxTable/AcxControlledTable";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthStore } from "../../../stores/AuthStore";
import { useStore } from "../../../utils/useStore";
import useOrganizations from "./hooks/useOrganizations";
import OrganizationArchiveConfirmationDialog from "./OrganizationArchiveConfirmationDialog";
import { Organization } from "./types/Organization.type";
import AcxSwitch from "components/UI/AcxSwitch";

const OrganizationsList = () => {
    const orgStore = useStore(AuthStore).orgStore;
    const [orgFilter, setOrgFilter] = useState<string>("");
    const [showInactiveTenants, setShowInactiveTenants] =
        useState<boolean>(false);
    const navigate = useNavigate();
    const { organizations, toggleOrganization, isLoading } = useOrganizations();
    const [filteredOrganizations, setFilteredOrganizations] = useState<
        Organization[]
    >([]);
    const [orgToArchive, setOrgToArchive] = useState<Organization | null>();

    useEffect(() => {
        let activeOrgs = organizations?.filter((org) => org.isActive === true);
        let orgList = !showInactiveTenants ? activeOrgs : organizations;

        const filtered = orgList?.filter((org) =>
            org.$search?.includes(orgFilter),
        );
        setFilteredOrganizations(filtered ? [...filtered] : []);
    }, [organizations, orgFilter, showInactiveTenants]);

    const columns = [
        {
            headerLabel: "Organization Name",
            dataKey: "name",
            formatter: (key, row) => (
                <Grid item>
                    <Link className="pendo-ignore"
                        onClick={async () => {
                            const organization = orgStore?.organizations?.find(
                                (org) => org.id === row.id,
                            );
                            if (!orgStore || !organization) return;

                            const didSelectOrganization =
                                await orgStore.setGlobalOrganization(
                                    organization,
                                );
                            if (didSelectOrganization) {
                                navigate(`/admin/organizations/${row.id}`);
                            }
                        }}
                        color="primary"
                        style={{ fontWeight: "bold" }}
                    >
                        {key}
                    </Link>
                </Grid>
            ),
        },
        {
            headerLabel: "",
            dataKey: "id",
            formatter: (key, row) => (
                <Grid
                    container
                    justifyContent="flex-end"
                    style={{ marginTop: -10, marginBottom: -10 }}
                >
                    <Grid item>
                        {!row.isActive && (
                            <Chip
                                size="small"
                                label="Inactive"
                                style={{ marginRight: 6 }}
                            />
                        )}
                    </Grid>
                </Grid>
            ),
        },
    ];

    const showInactiveToggle = (
        <FormControlLabel
            control={
                <AcxSwitch
                    checked={showInactiveTenants}
                    onClick={() => setShowInactiveTenants(!showInactiveTenants)}
                />
            }
            label="Display Inactive"
        />
    );

    const searchBox = (
        <AcxMainTextField
            id="orgsList-search"
            value=""
            placeholderText="Search..."
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                setOrgFilter(evt.currentTarget.value.toLowerCase())
            }
        />
    );

    return (
        <>
            <div>
                <AcxControlledTable
                    isLoading={isLoading}
                    skeletonRows={5}
                    rows={filteredOrganizations}
                    columns={columns}
                    rowTerm="Organizations"
                    enableStripes={false}
                    controls={[
                        showInactiveToggle,
                        searchBox,
                        {
                            controlElement: (
                                <AcxButton
                                    fullWidth
                                    style={{ height: "33px" }}
                                    color="primary"
                                    startIcon={<AddSharp />}
                                    onClick={() => {
                                        navigate("/admin/organizations/add");
                                    }}
                                >
                                    Add
                                </AcxButton>
                            ),
                            xs: 2,
                            sm: 2,
                            md: 2,
                            lg: 2,
                            xl: 2,
                        },
                    ]}
                />
            </div>

            {orgToArchive && (
                <OrganizationArchiveConfirmationDialog
                    isOpen={!!orgToArchive}
                    org={orgToArchive}
                    onClose={() => setOrgToArchive(null)}
                    toggleOrganization={toggleOrganization}
                />
            )}
        </>
    );
};

export default OrganizationsList;
